import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  ChartsTooltip,
  ChartsTooltipProps,
  useAxesTooltip,
} from "@mui/x-charts/ChartsTooltip";
import { MarkPlot } from "@mui/x-charts/LineChart";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import { useTranslation } from "react-i18next";
import { MissionControlTenant } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  SavingsProjection,
  useMissionControlTotalSavings,
} from "./useMissionControlTotalSavings";

const SPARKLINE_WIDTH = 180;
const SPARKLINE_HEIGHT = 46;
const TARGET_LINE_HEADROOM = 1.05;
const CURRENCY_MAX_DECIMALS = 0;
const DASH_PATTERN = "4 3";

const SavingsTooltip = (props: ChartsTooltipProps) => {
  const axes = useAxesTooltip();
  return axes?.[0]?.axisValue === 0 ? null : <ChartsTooltip {...props} />;
};

const getProjectionText = (
  projection: SavingsProjection,
  { targetLabel, deltaLabel }: { targetLabel: string; deltaLabel: string },
  t: (key: string, options?: Record<string, string>) => string
): string => {
  switch (projection.status) {
    case "hit":
      return projection.isComplete
        ? t("hitTarget", { target: targetLabel })
        : t("projectedToHitTarget", { target: targetLabel });
    case "exceeds":
      return projection.isComplete
        ? t("exceededTarget", { target: targetLabel, delta: deltaLabel })
        : t("projectedToExceedTarget", {
            target: targetLabel,
            delta: deltaLabel,
          });
    case "misses":
      return projection.isComplete
        ? t("missedTarget", { target: targetLabel, delta: deltaLabel })
        : t("projectedToMissTarget", {
            target: targetLabel,
            delta: deltaLabel,
          });
  }
};

export const MissionControlTotalSavingsCard = ({
  tenants,
  savingsTarget,
}: {
  tenants: MissionControlTenant[];
  savingsTarget: number;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const formatNumber = useFormatNumber();
  const formatMillions = (value: number): string =>
    `$${formatNumber(1)(value / 1_000_000)}M`;
  const formatTotalSavingsCurrency = (value: number): string =>
    `${value < 0 ? "-" : ""}$${formatNumber(CURRENCY_MAX_DECIMALS)(Math.abs(value))}`;
  const {
    isLoading,
    runningTotalSavings,
    dataPoints,
    fiscalYearLabels,
    projection,
    fiscalYearTwoDigit,
  } = useMissionControlTotalSavings(tenants, savingsTarget);

  const sparklineValues =
    dataPoints.length > 0 ? [0, ...dataPoints.map((point) => point.value)] : [];
  const color =
    runningTotalSavings >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  const hasTarget = savingsTarget > 0;
  const lastPoint = dataPoints[dataPoints.length - 1];
  const lastFiscalMonth = lastPoint?.fiscalMonth ?? 0;
  const maxIndex = fiscalYearLabels.length;
  const lastValue = lastPoint?.value ?? 0;
  const projectedEndValue = projection?.projectedEndValue ?? 0;
  const showPrediction = projection !== null;

  const dataMin = Math.min(0, ...sparklineValues);
  const dataMax = Math.max(...sparklineValues);
  const yMin = Math.min(
    dataMin,
    ...(hasTarget ? [savingsTarget] : []),
    showPrediction ? projectedEndValue : 0
  );
  const yMax = Math.max(
    dataMax,
    ...(hasTarget ? [savingsTarget * TARGET_LINE_HEADROOM] : []),
    showPrediction ? projectedEndValue : 0
  );
  const targetYPosition =
    yMax > yMin
      ? SPARKLINE_HEIGHT * (1 - (savingsTarget - yMin) / (yMax - yMin))
      : 0;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0 }}>
        {t("totalSavingsFromScrapOptimisation")}
      </Typography>
      {projection || hasTarget ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.25, display: "block", lineHeight: 1.2 }}
        >
          {projection
            ? getProjectionText(
                projection,
                {
                  targetLabel: formatMillions(savingsTarget),
                  deltaLabel: formatMillions(projection.delta),
                },
                t
              )
            : t("savingsTargetValue", {
                target: formatMillions(savingsTarget),
              })}
        </Typography>
      ) : (
        <Box sx={{ mb: 0.75 }} />
      )}
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            height: SPARKLINE_HEIGHT,
            flex: 2,
            minWidth: SPARKLINE_WIDTH,
            position: "relative",
          }}
        >
          {!isLoading && sparklineValues.length > 1 ? (
            <>
              <SparkLineChart
                data={sparklineValues}
                xAxis={{
                  scaleType: "linear",
                  data: [0, ...dataPoints.map((point) => point.fiscalMonth)],
                  min: 0,
                  max: maxIndex,
                  valueFormatter: (value: number) =>
                    fiscalYearLabels[Math.round(value) - 1] ?? "",
                }}
                yAxis={{ min: yMin, max: yMax }}
                margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                width={SPARKLINE_WIDTH}
                height={SPARKLINE_HEIGHT}
                curve="linear"
                area
                showTooltip
                slots={{ tooltip: SavingsTooltip }}
                valueFormatter={(value) =>
                  value != null ? formatTotalSavingsCurrency(value) : ""
                }
                slotProps={{
                  tooltip: {
                    sx: {
                      "& .MuiChartsTooltip-mark": {
                        borderColor: color,
                        backgroundColor: color,
                      },
                    },
                  },
                }}
                sx={{
                  "& .MuiLineElement-root": {
                    stroke: color,
                  },
                  "& .MuiAreaElement-root": {
                    fill: color,
                    fillOpacity: 0.35,
                  },
                  "& .MuiMarkElement-root": {
                    display: "none",
                  },
                  "& .MuiMarkElement-root:last-of-type": {
                    display: "block",
                    fill: color,
                    stroke: color,
                    r: 4,
                    transform: "translateX(-4px)",
                  },
                  "& .MuiHighlightElement-root": {
                    fill: color,
                    stroke: color,
                  },
                }}
              >
                <MarkPlot />
              </SparkLineChart>
              {hasTarget && yMax > yMin ? (
                <svg
                  width={SPARKLINE_WIDTH}
                  height={SPARKLINE_HEIGHT}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    pointerEvents: "none",
                  }}
                >
                  <line
                    x1={0}
                    y1={targetYPosition}
                    x2={SPARKLINE_WIDTH}
                    y2={targetYPosition}
                    stroke={theme.palette.grey[400]}
                    strokeWidth={1}
                    strokeDasharray={DASH_PATTERN}
                  />
                  {showPrediction ? (
                    <line
                      x1={(lastFiscalMonth / maxIndex) * SPARKLINE_WIDTH}
                      y1={
                        SPARKLINE_HEIGHT *
                        (1 - (lastValue - yMin) / (yMax - yMin))
                      }
                      x2={SPARKLINE_WIDTH}
                      y2={
                        SPARKLINE_HEIGHT *
                        (1 - (projectedEndValue - yMin) / (yMax - yMin))
                      }
                      stroke={color}
                      strokeWidth={1.5}
                      strokeDasharray={DASH_PATTERN}
                    />
                  ) : null}
                </svg>
              ) : null}
            </>
          ) : null}
        </Box>
        <Stack
          alignItems="flex-end"
          justifyContent="center"
          sx={{ py: 0.25, flex: 1 }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", color }}>
            {formatTotalSavingsCurrency(runningTotalSavings)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({t("fyYtd", { fiscalYearTwoDigits: fiscalYearTwoDigit })})
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
